/*
 * DD AdminUI
 * Copyright (C) Fraunhofer IESE 2019 - 2025 Stefan Schweitzer, Shyalan Ramesh, Rafael Aranda López King,
 * Florian Wege, Johannes Schneider, Kazi Rezoanur Rahman, Christoph Müller, Pedram Jokar,
 * Mher Ter-Tovmasyan, Ben Burkhard, Matthias Gerbershagen, Anna Kleiner, Muhammad Tariq,
 * Adeline Silva Schäfer
 *
 * SPDX-License-Identifier: Apache-2.0
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import React, { Component } from 'react';
import { connect } from 'react-redux';
import { PlatformAdminUIState, PlatformAdminUIThunkType } from '../../../store/store';
import AuthDetailsView from './AuthDetails.view';
import { RouteComponentProps } from 'react-router-dom';
import { Container } from '@mui/material';
import { getAuthUser } from '../store/User.actions';
import { ClientOauthAccount, ClientOauthAccountAuthenticationMethodsEnum } from '@DigitaleDoerfer/digitale-doerfer-api';
import { RelatedEntity } from '../store/User.state';
import { UserActionTypes } from '../store/UserActionTypes';
import PersonDetailFormView from '../../../shared/views/PersonDetail/PersonDetailForm.view';
import { USERS_URL } from '../UsersRouting.container';

interface Props extends RouteComponentProps {
	loading: boolean;
	selectedUser: ClientOauthAccount;
	relatedEntities: RelatedEntity[];
	getUser: (userId: string) => void;
}

interface Identifiable {
	email: string;
}

class AuthDetailsContainer extends Component<Props> {
	componentDidMount(): void {
		const { match, getUser } = this.props;
		const id = match.params as Identifiable;
		if (id.email) {
			getUser(id.email);
		}
	}

	gotToTableHandler = (): void => {
		this.props.history.push(USERS_URL);
	};

	render(): JSX.Element {
		const { loading, selectedUser, match } = this.props;
		const { email } = match.params as Identifiable;
		const title = `Auth-Account ${selectedUser && selectedUser.name ? selectedUser.name : email}`;
		const authenticationMethods: ClientOauthAccountAuthenticationMethodsEnum[] =
			(selectedUser && selectedUser.authenticationMethods && Array.from(selectedUser.authenticationMethods)) || [];
		return (
			<Container fixed maxWidth="md">
				<PersonDetailFormView
					title={title}
					textToCopy={selectedUser.oauthId ?? ''}
					authenticationMethods={authenticationMethods}
					goToTableHandler={this.gotToTableHandler}
				>
					<AuthDetailsView email={email} loading={loading} selectedUser={selectedUser} />
				</PersonDetailFormView>
			</Container>
		);
	}
}

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const mapStateToProps = (state: PlatformAdminUIState) => {
	return {
		loading: state.user.authDetail.loading,
		selectedUser: state.user.authDetail.authUser
	};
};

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const mapDispatchToProps = (dispatch: PlatformAdminUIThunkType<UserActionTypes>) => {
	return {
		getUser: (email: string): Promise<void> => dispatch(getAuthUser(email))
	};
};

export default connect(mapStateToProps, mapDispatchToProps)(AuthDetailsContainer);
